/**
 * Utility functions for CSV parsing and validation
 */

import type { ColumnDefinition, CsvImportResult } from "./types";

/**
 * Validates CSV data based on column definitions
 */
export function validateCsvData<T extends Record<string, any>>(
  data: T[],
  columns: ColumnDefinition<T>[]
): string[] {
  const errors: string[] = [];

  data.forEach((row, index) => {
    const rowNumber = index + 2; // +2 because index 0 is header

    columns.forEach((col) => {
      if (col.required && !row[col.key]) {
        errors.push(`Row ${rowNumber}: ${col.label} is required`);
      }
      if (row[col.key] && col.validate) {
        const error = col.validate(row[col.key]);
        if (error) {
          errors.push(`Row ${rowNumber}: ${error}`);
        }
      }
    });
  });

  return errors;
}

/**
 * Parses CSV text into array of row objects
 */
export function parseCsv<T extends Record<string, any>>(csvText: string): T[] {
  const lines = csvText.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, ""));
  const data: T[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = line.split(",").map((v) => v.trim().replace(/"/g, ""));
    const row: any = {};

    headers.forEach((header, index) => {
      row[header] = values[index] || "";
    });

    data.push(row as T);
  }

  return data;
}

/**
 * Downloads a CSV file
 */
export function downloadSampleCsv(
  sampleCsv: string,
  filename: string = "import_template.csv"
): void {
  const blob = new Blob([sampleCsv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
